import { useState, useEffect, useRef } from 'react';
import { uploadMemoryAudio } from '../../../api/memories';
import type { MemoryRead } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';

type Step = 'record' | 'date' | 'photos';

interface DateOption {
  id: string;
  label: string;
}

const DATE_OPTIONS: DateOption[] = [
  { id: 'today', label: 'Сегодня' },
  { id: 'yesterday', label: 'Вчера' },
  { id: 'custom', label: 'Указать дату' },
];

const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function getDayOfWeek(year: number, month: number): number {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

interface LocalPhoto {
  file: File;
  url: string;
}

interface CreateMemoryScreenProps {
  onSaved?: (memory: MemoryRead) => void;
}

function pickMimeType(): string | undefined {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function formatDateRu(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export default function CreateMemoryScreen({ onSaved }: CreateMemoryScreenProps) {
  const today = useRef(new Date()).current;
  const [step, setStep] = useState<Step>('record');
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [waveHeights, setWaveHeights] = useState<number[]>(Array.from({ length: 32 }, () => 10));
  const [recordingDone, setRecordingDone] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('today');
  const [showCalendar, setShowCalendar] = useState(false);
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calSelected, setCalSelected] = useState<number | null>(null);
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const secondsRef = useRef(0);
  const photosRef = useRef<LocalPhoto[]>([]);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          secondsRef.current = s + 1;
          return s + 1;
        });
      }, 1000);
      waveRef.current = setInterval(() => {
        setWaveHeights(Array.from({ length: 32 }, () => Math.floor(Math.random() * 50) + 8));
      }, 110);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (waveRef.current) clearInterval(waveRef.current);
      if (!recordingDone) setWaveHeights(Array.from({ length: 32 }, () => 10));
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (waveRef.current) clearInterval(waveRef.current);
    };
  }, [isRecording, recordingDone]);

  const formatTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => stopStream, []);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => () => {
    photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.url));
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const showToast = (nextToast: { tone: 'success' | 'error'; text: string }) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(nextToast);
    toastTimerRef.current = setTimeout(() => setToast(null), 2000);
  };

  const resetForm = (photosToRevoke: LocalPhoto[] = photos) => {
    setStep('record');
    setIsRecording(false);
    setSeconds(0);
    setRecordingDone(false);
    setSelectedDate('today');
    setShowCalendar(false);
    setCalSelected(null);
    photosToRevoke.forEach((photo) => URL.revokeObjectURL(photo.url));
    setPhotos([]);
    setAudioBlob(null);
    setSaveError(null);
    setWaveHeights(Array.from({ length: 32 }, () => 10));
  };

  const startRecording = async () => {
    setSaveError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setSaveError('Браузер не поддерживает запись звука. Попробуйте Chrome или Safari новой версии.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      secondsRef.current = 0;
      setSeconds(0);
      setAudioBlob(null);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        stopStream();
        setRecordingDone(true);
        setTimeout(() => {
          setStep('date');
        }, 500);
      };

      recorder.start();
      setRecordingDone(false);
      setIsRecording(true);
    } catch {
      stopStream();
      setSaveError('Не получилось получить доступ к микрофону. Проверьте разрешения браузера.');
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setIsRecording(false);
  };

  const handleToggleRecord = () => {
    if (saving || userLoading || !userId) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleDateSelect = (id: string) => {
    setSelectedDate(id);
    if (id === 'custom') {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
    }
  };

  const handleDateConfirm = () => {
    setStep('photos');
  };

  const selectedEventDate = () => {
    if (selectedDate === 'yesterday') return toDateInputValue(addDays(today, -1));
    if (selectedDate === 'custom' && calSelected) {
      return toDateInputValue(new Date(calYear, calMonth, calSelected));
    }
    return toDateInputValue(today);
  };

  const handlePhotosConfirm = async () => {
    if (!userId || !audioBlob || saving) return;

    const uploadAudio = audioBlob;
    const uploadEventDate = selectedEventDate();
    const uploadDuration = secondsRef.current;
    const uploadPhotos = photos.map((photo) => photo.file);
    const photosToRevoke = photos;

    setSaving(true);
    setSaveError(null);
    setStep('record');
    try {
      const result = await uploadMemoryAudio({
        userId,
        audioBlob: uploadAudio,
        eventDate: uploadEventDate,
        durationSeconds: uploadDuration,
        photos: uploadPhotos,
        processSync: false,
      });
      onSaved?.(result.memory);
      resetForm(photosToRevoke);
      showToast({
        tone: 'success',
        text: 'Ваше воспоминание сохранено и скоро появится в истории.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось сохранить воспоминание';
      setSaveError(message);
      showToast({ tone: 'error', text: message });
    } finally {
      setSaving(false);
    }
  };

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const available = 10 - photos.length;
    if (available <= 0) return;
    const next = Array.from(files).slice(0, available).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...next]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, photoIndex) => photoIndex !== index);
    });
  };

  const frozenWave = useRef<number[]>(Array.from({ length: 32 }, () => 10));
  useEffect(() => {
    if (recordingDone && !isRecording) {
      frozenWave.current = waveHeights;
    }
  }, [recordingDone, isRecording, waveHeights]);

  const displayWave = recordingDone && !isRecording ? frozenWave.current : waveHeights;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 20px 12px',
          background: 'rgba(26,27,46,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '-0.2px' }}>
          Создать воспоминание
        </span>
      </div>

      {/* ── Main recording content ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 32px',
          gap: '0px',
          overflow: 'hidden',
        }}
      >
        {/* Helper text */}
        <p
          style={{
            fontSize: '14px',
            color: '#6B7A94',
            lineHeight: 1.7,
            textAlign: 'center',
            marginBottom: '40px',
            maxWidth: '280px',
            transition: 'opacity 0.3s ease',
            opacity: isRecording ? 0 : 1,
          }}
        >
          Нажмите на кнопку и в свободной форме расскажите о событии, которое хотите сохранить.
        </p>

        {/* Timer */}
        <div style={{ height: '32px', display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          {isRecording && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '8px', height: '8px', borderRadius: '50%', background: '#FF6B6B',
                  animation: 'blinkDot 1s ease-in-out infinite',
                }}
              />
              <span
                style={{
                  fontSize: '22px', fontWeight: 600, color: '#F5F5F7',
                  fontVariantNumeric: 'tabular-nums', letterSpacing: '2px',
                }}
              >
                {formatTime(seconds)}
              </span>
            </div>
          )}
        </div>

        {/* Record button */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!isRecording && !recordingDone && (
            <>
              <div style={{
                position: 'absolute', width: '158px', height: '158px', borderRadius: '50%',
                border: '1.5px solid rgba(62,207,160,0.2)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', width: '192px', height: '192px', borderRadius: '50%',
                border: '1px solid rgba(62,207,160,0.1)', pointerEvents: 'none',
              }} />
            </>
          )}
          {isRecording && (
            <div style={{
              position: 'absolute', width: '158px', height: '158px', borderRadius: '50%',
              border: '2px solid rgba(255,107,107,0.3)', pointerEvents: 'none',
              animation: 'pulseRing 1.5s ease-in-out infinite',
            }} />
          )}

          <button
            onClick={handleToggleRecord}
            disabled={saving || userLoading || !userId}
            style={{
              width: '124px',
              height: '124px',
              borderRadius: isRecording ? '28px' : '50%',
              background: isRecording
                ? 'linear-gradient(135deg, #FF6B6B, #EE5A52)'
                : recordingDone
                  ? 'linear-gradient(135deg, #4A90E2, #3574C4)'
                  : 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
              border: 'none',
              cursor: saving || userLoading || !userId ? 'default' : 'pointer',
              opacity: saving || userLoading || !userId ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isRecording
                ? '0 8px 32px rgba(255,107,107,0.45)'
                : recordingDone
                  ? '0 8px 32px rgba(74,144,226,0.4)'
                  : '0 8px 32px rgba(62,207,160,0.45)',
              transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {isRecording
              ? <div style={{ width: '34px', height: '34px', borderRadius: '6px', background: '#fff' }} />
              : recordingDone
                ? <i className="ri-check-line" style={{ fontSize: '48px', color: '#fff' }} />
                : <i className="ri-mic-fill" style={{ fontSize: '48px', color: '#fff' }} />
            }
          </button>
        </div>

        {/* Label */}
        <p
          style={{
            fontSize: '16px', fontWeight: 600, marginTop: '24px',
            color: isRecording ? '#FF6B6B' : recordingDone ? '#4A90E2' : '#F5F5F7',
            transition: 'color 0.3s ease',
          }}
        >
          {isRecording
            ? 'Запись идёт'
            : saving
              ? 'Сохраняем воспоминание'
            : recordingDone
              ? 'Записано'
              : userLoading || !userId
                ? 'Подключаем Омут Памяти'
                : 'Начать запись'}
        </p>

        {(saveError || userError) && (
          <p style={{ fontSize: '13px', color: '#FFB4B4', textAlign: 'center', lineHeight: 1.5, maxWidth: '280px', marginTop: '10px' }}>
            {saveError || userError}
          </p>
        )}

        {/* Waveform */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            height: '64px',
            marginTop: '20px',
            opacity: isRecording || recordingDone ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        >
          {displayWave.map((h, i) => (
            <div
              key={i}
              style={{
                width: '3px',
                height: `${h}px`,
                borderRadius: '2px',
                background: isRecording
                  ? `linear-gradient(to top, rgba(255,107,107,0.2), #FF6B6B)`
                  : `linear-gradient(to top, rgba(74,144,226,0.2), #4A90E2)`,
                transition: isRecording ? 'height 0.11s ease' : 'background 0.3s ease',
              }}
            />
          ))}
        </div>

        {/* Bottom hint */}
        <p
          style={{
            fontSize: '13px', color: '#6B7A94', textAlign: 'center',
            marginTop: '20px', lineHeight: 1.5,
          }}
        >
          {isRecording
            ? 'Нажмите ещё раз, чтобы завершить запись'
            : recordingDone
              ? 'Запись завершена — продолжите ниже'
              : 'Говорите свободно — никто кроме вас не услышит'}
        </p>
      </div>

      {/* ── Bottom sheets ── */}
      {step === 'date' && (
        <BottomSheet onClose={() => { setStep('record'); setRecordingDone(false); }}>
          <DateStep
            selected={selectedDate}
            today={today}
            showCalendar={showCalendar}
            calYear={calYear}
            calMonth={calMonth}
            calSelected={calSelected}
            onSelect={handleDateSelect}
            onCalPrev={() => {
              if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
              else setCalMonth(m => m - 1);
            }}
            onCalNext={() => {
              if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
              else setCalMonth(m => m + 1);
            }}
            onCalDay={(d) => setCalSelected(d)}
            onConfirm={handleDateConfirm}
          />
        </BottomSheet>
      )}

      {step === 'photos' && (
        <BottomSheet onClose={() => { if (!saving) setStep('date'); }}>
          <PhotosStep
            photos={photos}
            saving={saving}
            error={saveError}
            onAddPhoto={addPhotos}
            onRemovePhoto={removePhoto}
            onConfirm={handlePhotosConfirm}
            onSkip={handlePhotosConfirm}
          />
        </BottomSheet>
      )}

      {toast && (
        <div
          style={{
            position: 'absolute',
            left: '18px',
            right: '18px',
            bottom: 'calc(96px + env(safe-area-inset-bottom))',
            zIndex: 220,
            borderRadius: '18px',
            padding: '14px 16px',
            background: toast.tone === 'success'
              ? 'rgba(62,207,160,0.96)'
              : 'rgba(255,107,107,0.96)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: toast.tone === 'success'
              ? '0 12px 32px rgba(62,207,160,0.35)'
              : '0 12px 32px rgba(255,107,107,0.3)',
            animation: 'toastIn 0.22s ease',
          }}
        >
          <i
            className={toast.tone === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}
            style={{ fontSize: '20px', flexShrink: 0 }}
          />
          <span style={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.45 }}>
            {toast.text}
          </span>
        </div>
      )}

      <style>{`
        @keyframes blinkDot { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes pulseRing { 0%{transform:scale(1);opacity:0.6} 70%{transform:scale(1.15);opacity:0} 100%{transform:scale(1.15);opacity:0} }
        @keyframes toastIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

/* ── Bottom Sheet wrapper ── */
function BottomSheet({
  children,
  onClose,
  hideClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
  hideClose?: boolean;
}) {
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)', zIndex: 100,
        }}
      />
      {/* Sheet */}
      <div
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 101,
          background: 'linear-gradient(180deg, #232438 0%, #1E1F35 100%)',
          borderRadius: '24px 24px 0 0',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          padding: '0 0 calc(32px + env(safe-area-inset-bottom)) 0',
          maxHeight: '82%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '12px', paddingBottom: '4px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.2)' }} />
        </div>
        {!hideClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: '16px', right: '20px',
              width: '30px', height: '30px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <i className="ri-close-line" style={{ color: '#A8B2C8', fontSize: '16px' }} />
          </button>
        )}
        {children}
      </div>
    </>
  );
}

/* ── Step 1: Date ── */
interface DateStepProps {
  selected: string;
  today: Date;
  showCalendar: boolean;
  calYear: number;
  calMonth: number;
  calSelected: number | null;
  onSelect: (id: string) => void;
  onCalPrev: () => void;
  onCalNext: () => void;
  onCalDay: (d: number) => void;
  onConfirm: () => void;
}

function DateStep({ selected, today, showCalendar, calYear, calMonth, calSelected, onSelect, onCalPrev, onCalNext, onCalDay, onConfirm }: DateStepProps) {
  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const startOffset = getDayOfWeek(calYear, calMonth);

  const canConfirm = selected !== 'custom' || calSelected !== null;
  const optionSub = (id: string) => {
    if (id === 'today') return formatDateRu(today);
    if (id === 'yesterday') return formatDateRu(addDays(today, -1));
    if (calSelected) return formatDateRu(new Date(calYear, calMonth, calSelected));
    return 'Выбрать в календаре';
  };

  return (
    <div style={{ padding: '8px 20px 0' }}>
      {/* Icon */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'rgba(62,207,160,0.15)', border: '1px solid rgba(62,207,160,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="ri-calendar-check-line" style={{ fontSize: '24px', color: '#3ECFA0' }} />
        </div>
      </div>

      <p style={{ fontSize: '20px', fontWeight: 700, color: '#F5F5F7', textAlign: 'center', marginBottom: '6px' }}>
        Воспоминание записано
      </p>
      <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', marginBottom: '24px', lineHeight: 1.5 }}>
        Когда это произошло?
      </p>

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        {DATE_OPTIONS.map((opt) => {
          const isActive = selected === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => onSelect(opt.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px', borderRadius: '16px', cursor: 'pointer',
                background: isActive ? 'rgba(62,207,160,0.15)' : 'rgba(255,255,255,0.05)',
                border: isActive ? '1.5px solid rgba(62,207,160,0.5)' : '1.5px solid rgba(255,255,255,0.08)',
                transition: 'all 0.2s ease',
              }}
            >
              <div>
                <p style={{ fontSize: '15px', fontWeight: 600, color: isActive ? '#3ECFA0' : '#F5F5F7', marginBottom: '2px' }}>{opt.label}</p>
                <p style={{ fontSize: '12px', color: isActive ? 'rgba(62,207,160,0.7)' : '#6B7A94' }}>{optionSub(opt.id)}</p>
              </div>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%',
                border: isActive ? '2px solid #3ECFA0' : '2px solid rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isActive ? '#3ECFA0' : 'transparent',
              }}>
                {isActive && <i className="ri-check-line" style={{ fontSize: '13px', color: '#fff' }} />}
              </div>
            </button>
          );
        })}
      </div>

      {/* Calendar picker */}
      {showCalendar && (
        <div
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '16px',
          }}
        >
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <button onClick={onCalPrev} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
              <i className="ri-arrow-left-s-line" style={{ fontSize: '20px', color: '#A8B2C8' }} />
            </button>
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7' }}>
              {MONTHS_RU[calMonth]} {calYear}
            </span>
            <button onClick={onCalNext} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
              <i className="ri-arrow-right-s-line" style={{ fontSize: '20px', color: '#A8B2C8' }} />
            </button>
          </div>

          {/* Weekday headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '6px' }}>
            {WEEKDAYS_RU.map((d) => (
              <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 500, color: '#6B7A94' }}>{d}</div>
            ))}
          </div>

          {/* Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
            {Array.from({ length: startOffset }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const d = i + 1;
              const isActive = calSelected === d;
              return (
                <button
                  key={d}
                  onClick={() => onCalDay(d)}
                  style={{
                    aspectRatio: '1', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? '#3ECFA0' : 'transparent',
                    border: 'none', cursor: 'pointer',
                    fontSize: '13px', fontWeight: isActive ? 700 : 400,
                    color: isActive ? '#fff' : '#A8B2C8',
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirm */}
      <button
        onClick={onConfirm}
        disabled={!canConfirm}
        style={{
          width: '100%', padding: '16px', borderRadius: '16px', border: 'none',
          background: canConfirm ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
          color: canConfirm ? '#fff' : '#4A5568',
          fontSize: '16px', fontWeight: 600, cursor: canConfirm ? 'pointer' : 'default',
          boxShadow: canConfirm ? '0 8px 24px rgba(62,207,160,0.3)' : 'none',
          transition: 'all 0.2s ease',
        }}
      >
        Продолжить
      </button>
    </div>
  );
}

/* ── Step 2: Photos ── */
interface PhotosStepProps {
  photos: LocalPhoto[];
  saving: boolean;
  error: string | null;
  onAddPhoto: (files: FileList | null) => void;
  onRemovePhoto: (index: number) => void;
  onConfirm: () => void;
  onSkip: () => void;
}

function PhotosStep({ photos, saving, error, onAddPhoto, onRemovePhoto, onConfirm, onSkip }: PhotosStepProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openPicker = () => inputRef.current?.click();

  return (
    <div style={{ padding: '8px 20px 0' }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(event) => {
          onAddPhoto(event.target.files);
          event.target.value = '';
        }}
        style={{ display: 'none' }}
      />
      {/* Icon */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'rgba(139,122,184,0.15)', border: '1px solid rgba(139,122,184,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <i className="ri-image-line" style={{ fontSize: '24px', color: '#8B7AB8' }} />
        </div>
      </div>

      <p style={{ fontSize: '20px', fontWeight: 700, color: '#F5F5F7', textAlign: 'center', marginBottom: '6px' }}>
        Добавьте фотографии
      </p>
      <p style={{ fontSize: '14px', color: '#6B7A94', textAlign: 'center', marginBottom: '24px', lineHeight: 1.5 }}>
        Хотите добавить фотографии к этому воспоминанию?
      </p>

      {/* Photo thumbnails */}
      {photos.length > 0 && (
        <div
          style={{
            display: 'flex', gap: '8px', overflowX: 'auto',
            marginBottom: '16px', paddingBottom: '4px',
          }}
        >
          {photos.map((photo, i) => (
            <div
              key={i}
              style={{
                width: '72px', height: '72px', borderRadius: '12px',
                overflow: 'hidden', flexShrink: 0,
                border: '1.5px solid rgba(139,122,184,0.4)',
                position: 'relative',
              }}
            >
              <img src={photo.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button
                onClick={() => onRemovePhoto(i)}
                disabled={saving}
                style={{
                  position: 'absolute', top: '4px', right: '4px',
                  width: '22px', height: '22px', borderRadius: '50%',
                  border: 'none', background: 'rgba(0,0,0,0.55)',
                  color: '#fff', cursor: saving ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className="ri-close-line" style={{ fontSize: '14px' }} />
              </button>
            </div>
          ))}
          {photos.length < 10 && (
            <button
              onClick={openPicker}
              disabled={saving}
              style={{
                width: '72px', height: '72px', borderRadius: '12px',
                border: '1.5px dashed rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: saving ? 'default' : 'pointer', flexShrink: 0,
                opacity: saving ? 0.55 : 1,
              }}
            >
              <i className="ri-add-line" style={{ fontSize: '24px', color: '#6B7A94' }} />
            </button>
          )}
        </div>
      )}

      {/* Upload button */}
      {photos.length === 0 && (
        <button
          onClick={openPicker}
          disabled={saving}
          style={{
            width: '100%', padding: '16px', borderRadius: '16px',
            background: 'rgba(139,122,184,0.15)',
            border: '1.5px dashed rgba(139,122,184,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '10px', cursor: saving ? 'default' : 'pointer', marginBottom: '12px',
            opacity: saving ? 0.55 : 1,
          }}
        >
          <i className="ri-upload-cloud-line" style={{ fontSize: '22px', color: '#8B7AB8' }} />
          <span style={{ fontSize: '15px', fontWeight: 600, color: '#8B7AB8' }}>Загрузить фотографии</span>
        </button>
      )}

      <p style={{ fontSize: '12px', color: '#6B7A94', textAlign: 'center', marginBottom: '20px' }}>
        Можно добавить до 10 фотографий
      </p>

      {error && (
        <p style={{ fontSize: '13px', color: '#FFB4B4', textAlign: 'center', lineHeight: 1.5, marginBottom: '14px' }}>
          {error}
        </p>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <button
          onClick={onConfirm}
          disabled={photos.length === 0 || saving}
          style={{
            width: '100%', padding: '16px', borderRadius: '16px', border: 'none',
            background: photos.length > 0 && !saving ? 'linear-gradient(135deg, #8B7AB8, #6B5A98)' : 'rgba(255,255,255,0.06)',
            color: photos.length > 0 && !saving ? '#fff' : '#4A5568',
            fontSize: '16px', fontWeight: 600,
            cursor: photos.length > 0 && !saving ? 'pointer' : 'default',
            boxShadow: photos.length > 0 && !saving ? '0 8px 24px rgba(139,122,184,0.3)' : 'none',
            transition: 'all 0.2s ease',
          }}
        >
          {saving ? 'Сохраняем...' : 'Сохранить с фото'}
        </button>
        <button
          onClick={onSkip}
          disabled={saving}
          style={{
            width: '100%', padding: '15px', borderRadius: '16px',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#A8B2C8', fontSize: '15px', fontWeight: 500,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.55 : 1,
          }}
        >
          {saving ? 'Сохраняем...' : 'Пропустить'}
        </button>
      </div>
    </div>
  );
}
